/**
 * GSAP 入口、插件注册、全局默认值。
 *
 * 纪律（本项目最容易破的一条）：**只有 src/motion/ 目录可以 import gsap**。
 * 组件里出现 `gsap.xxx` 就是违规。这条规矩换来：
 *   · 动效可以整体替换，改一个目录就够
 *   · 不会出现"组件卸载了动画还在跑"这类幽灵 bug
 *   · 性能问题有唯一排查入口
 *
 * 为什么只用 GSAP、不混 framer-motion：
 * 两套引擎在同一棵组件树里会抢同一批帧，抖动和掉帧很难查。
 * GSAP 一套就能覆盖从按压反馈到滚动编排的全部需求。
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * 全局动效参数。所有 hook 都从这里取值，不要在调用点硬编码数字，
 * 否则调一次手感要满仓库改。
 */
export const MOTION = {
	/** 按压下去的收缩时长 */
	pressIn: 0.12,
	/** 松手回弹，带一点回冲才有物理感 */
	pressOut: 0.34,
	/** 元素入场 */
	enter: 0.44,
	/** 列表逐条入场 */
	stagger: 0.045,
	/** 列表增删的补位 */
	flip: 0.36,
	/** 元素退场 */
	exit: 0.24,
	/** 面板上滑 */
	sheetIn: 0.46,
	/** 面板回弹归位 */
	sheetBack: 0.52,
	/** 标签切换淡入 */
	page: 0.34,
	ease: {
		out: 'power3.out',
		expo: 'expo.out',
		in: 'power2.in',
		/** 停手时的回冲，用 back 而不是 elastic，elastic 会晃 */
		back: 'back.out(2.4)',
		soft: 'power2.out',
	},
} as const;

gsap.defaults({ duration: MOTION.enter, ease: MOTION.ease.out });

/** 尊重系统的"减弱动态效果"。开了就一律不动画，只做瞬时状态切换。 */
export function prefersReducedMotion(): boolean {
	if (typeof window === 'undefined' || !window.matchMedia) return false;
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function isReducedTransparency(): boolean {
	if (typeof window === 'undefined' || !window.matchMedia) return false;
	return window.matchMedia('(prefers-reduced-transparency: reduce)').matches;
}

/** 元素从视口外进入时的统一起点 */
export const ENTER_FROM = { autoAlpha: 0, y: 12 } as const;
export const ENTER_TO = { autoAlpha: 1, y: 0 } as const;

export { gsap, ScrollTrigger };
